import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { PulseLoader } from 'react-spinners';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-lg border font-semibold whitespace-nowrap transition-all outline-none disabled:cursor-not-allowed disabled:border-border-disabled disabled:bg-slate-200 disabled:text-slate-500 disabled:hover:bg-slate-200 disabled:hover:text-slate-500 has-[>svg]:px-2.5 data-[state=disabled]:cursor-not-allowed data-[state=disabled]:border-border-disabled data-[state=disabled]:bg-slate-200 data-[state=disabled]:text-slate-500 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&>svg]:size-5',
  {
    variants: {
      variant: {
        brand:
          'bg-background-brand text-content-primary-inverse hover:bg-background-brand-hover data-[state=selected]:bg-background-brand-hover',
        accent:
          'bg-background-cyan-bold text-content-primary-inverse hover:bg-background-cyan-bold-hover',
        secondary:
          'bg-background-primary text-content-secondary hover:text-content-brand-hover hover:bg-background-secondary border-border-secondary border',
        tertiary:
          'bg-background-secondary text-content-secondary hover:text-content-secondary hover:bg-background-tertiary border-border-primary border data-[state=selected]:bg-background-tertiary-hover data-[state=selected]:text-content-brand-hover',
        inverse:
          'border border-border-brand-inverse text-content-brand-bold bg-background-primary hover:bg-background-brand-inverse hover:text-content-brand-extra-bold data-[state=selected]:bg-background-brand-inverse data-[state=selected]:text-content-brand-extra-bold',
        ghost:
          'border-0 text-content-brand hover:bg-background-brand-inverse hover:text-content-brand-hover data-[state=selected]:bg-background-brand-inverse data-[state=selected]:text-content-brand-hover',
        link: '-mx-2.5 border-0 text-content-brand hover:text-content-brand-hover data-[state=selected]:text-content-brand-hover',
        warning:
          'border border-border-negative text-content-negative hover:bg-background-negative hover:text-content-negative-bold',
      },
      size: {
        xs: 'h-8 text-xs rounded-md gap-1.5 px-2.5 has-[>svg]:px-2.5 [&>svg]:size-5',
        sm: 'h-14 tablet:h-10 text-sm rounded-md gap-1.5 px-2.5 has-[>svg]:px-2.5 [&>svg]:size-5',
        base: 'h-12 px-3.5 py-2 has-[>svg]:px-3 [&>svg]:size-5',
        lg: 'h-14 rounded-md px-6 has-[>svg]:px-4',
        'icon-xs': 'size-7',
        'icon-sm': 'size-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'brand',
      size: 'base',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? <PulseLoader color='currentColor' size={8} /> : children}
    </Comp>
  );
}

export { Button, buttonVariants };
